import type { SimulationResult } from '../engine/types'

interface Props {
  result: SimulationResult
  startYear: number
  retirementTarget: number
  inflationMean: number
}

function fmt(v: number): string {
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(2)}M`
  if (v >= 1_000) return `$${(v / 1_000).toFixed(0)}k`
  return `$${Math.round(v)}`
}

/** HC-CONSTANT-CURRENCY: deflate nominal value to startYear real dollars. yearIndex is 0-based. */
function real(nominal: number, yearIndex: number, inflationMean: number): number {
  return nominal / Math.pow(1 + inflationMean, yearIndex)
}

function pct(v: number): string {
  return `${(v * 100).toFixed(1)}%`
}

export function ScenarioSummary({ result, startYear, retirementTarget, inflationMean }: Props) {
  const yr1 = result.netWorthBands[0]
  const yr5 = result.netWorthBands[Math.min(4, result.netWorthBands.length - 1)]
  const yr10 = result.netWorthBands[Math.min(9, result.netWorthBands.length - 1)]
  const yr20 = result.netWorthBands[Math.min(19, result.netWorthBands.length - 1)]
  const lastYr = result.netWorthBands[result.netWorthBands.length - 1]

  const income1 = result.incomeBands[0]
  const savings1 = result.savingsBands[0]

  // BandStat index constants (0-based year offset into netWorthBands)
  const IDX_1 = 0
  const IDX_5 = Math.min(4, result.netWorthBands.length - 1)
  const IDX_10 = Math.min(9, result.netWorthBands.length - 1)
  const IDX_20 = Math.min(19, result.netWorthBands.length - 1)
  const IDX_LAST = result.netWorthBands.length - 1

  return (
    <div className="summary-card">
      <h3>Scenario Summary <span className="real-dollars-badge">{startYear} real dollars</span></h3>

      <div className="summary-grid">
        <SummaryBlock title="Year 1 Income (median)">
          <Stat label="Gross income" value={fmt(real(income1.p50, 0, inflationMean))} />
          <Stat label="Net savings" value={fmt(real(savings1.p50, 0, inflationMean))} />
        </SummaryBlock>

        <SummaryBlock title="Retirement milestone">
          <Stat
            label="Success probability"
            value={pct(result.successProbability)}
            highlight={result.successProbability >= 0.8 ? 'good' : result.successProbability >= 0.5 ? 'warn' : 'bad'}
          />
          <Stat
            label="Median year to target"
            value={
              result.medianRetirementYear
                ? String(result.medianRetirementYear)
                : 'Not reached'
            }
          />
          <Stat label={`Target (${startYear} real $)`} value={fmt(retirementTarget)} />
        </SummaryBlock>

        <SummaryBlock title={`Net worth at year +1 (${startYear + 1})`}>
          <BandStat band={yr1} idx={IDX_1} inflationMean={inflationMean} />
        </SummaryBlock>

        <SummaryBlock title={`Net worth at year +5 (${startYear + 5})`}>
          <BandStat band={yr5} idx={IDX_5} inflationMean={inflationMean} />
        </SummaryBlock>

        <SummaryBlock title={`Net worth at year +10 (${startYear + 10})`}>
          <BandStat band={yr10} idx={IDX_10} inflationMean={inflationMean} />
        </SummaryBlock>

        {result.netWorthBands.length > 19 && (
          <SummaryBlock title={`Net worth at year +20 (${startYear + 20})`}>
            <BandStat band={yr20} idx={IDX_20} inflationMean={inflationMean} />
          </SummaryBlock>
        )}

        <SummaryBlock title={`Net worth at year +${result.netWorthBands.length} (${lastYr.year})`}>
          <BandStat band={lastYr} idx={IDX_LAST} inflationMean={inflationMean} />
        </SummaryBlock>
      </div>

      <p className="run-meta">
        {result.iterations.toLocaleString()} Monte Carlo iterations ·{' '}
        {result.netWorthBands.length} year projection from {startYear}
      </p>
    </div>
  )
}

// ─── Small helpers ────────────────────────────────────────────────────────────

function SummaryBlock({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="summary-block">
      <h4>{title}</h4>
      {children}
    </div>
  )
}

function Stat({
  label,
  value,
  highlight,
}: {
  label: string
  value: string
  highlight?: 'good' | 'warn' | 'bad'
}) {
  return (
    <div className={`stat-row ${highlight ?? ''}`}>
      <span className="stat-label">{label}</span>
      <span className="stat-value">{value}</span>
    </div>
  )
}

function BandStat({
  band,
  idx,
  inflationMean,
}: {
  band: { p10: number; p25: number; p50: number; p75: number; p90: number }
  idx: number
  inflationMean: number
}) {
  return (
    <>
      <Stat label="p90" value={fmt(real(band.p90, idx, inflationMean))} />
      <Stat label="p75" value={fmt(real(band.p75, idx, inflationMean))} />
      <Stat label="Median" value={fmt(real(band.p50, idx, inflationMean))} highlight="good" />
      <Stat label="p25" value={fmt(real(band.p25, idx, inflationMean))} />
      <Stat label="p10" value={fmt(real(band.p10, idx, inflationMean))} />
    </>
  )
}
